import { useState } from 'react';
import { Button, Grid, IconButton, Link, Paper, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';
import moment from 'moment';

import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import XLSX from 'xlsx';
import LoadingSpinner from '../components/LoadingSpinner';
import { GET_TRIAL_CONVERSION_REPORT } from './reports.graphql';
import ExcelLogo from '../assets/microsoft-excel.svg?react';
import { GetTrialConversionReportQuery } from '../__generated__/graphql';

interface TrialConversionReportModel {
    totalTrialUsersInPeriod: number;
    trialUsersWithPaidSubscription: number;
    trialUsersWithSingleDocumentAccess: number;
    trialUsersWithBoth: number;
    trialUsersWithAnyPurchase: number;
    convertedUsers: TrialConversionReportEntry[];
}

interface TrialConversionReportEntry {
    email: string;
    trialCreatedAt: string;
    trialLicenseId: number;
    trialLicenseName: string;
    trialSubscriptionId: number;
    trialSubscriptionName: string;
    convertedAt: string;
    convertedLicenseId: number | null;
    convertedLicenseName: string | null;
    totalAmountPaid: number;
    convertedSubscriptionId: number | null;
    convertedSubscriptionName: string | null;
    conversionType: 'PaidSubscription' | 'SingleDocumentAccess';
    title: string | null;
}

type TrialConversionMetricId =
    | 'totalTrialUsersInPeriod'
    | 'trialUsersWithPaidSubscription'
    | 'trialUsersWithSingleDocumentAccess'
    | 'trialUsersWithBoth'
    | 'trialUsersWithAnyPurchase';

interface TrialConversionTableRow {
    id: TrialConversionMetricId;
    metric: string;
    value: number;
    supportsDrillDown: boolean;
}

interface TrialConversionDetailRow {
    id: string;
    paidCustomerNames: string;
    paidSubscriptionNames: string;
    totalAmountPaid: number;
    paidCustomers: TrialConversionCustomerLink[];
    hasPaidSubscription: boolean;
    hasSingleDocumentAccess: boolean;
}

interface TrialConversionCustomerLink {
    licenseId: number | null;
    name: string;
}

interface TrialConversionReportQueryModel {
    __typename?: string;
    totalTrialUsersInPeriod?: number | null;
    trialUsersWithPaidSubscription?: number | null;
    trialUsersWithSingleDocumentAccess?: number | null;
    trialUsersWithBoth?: number | null;
    trialUsersWithAnyPurchase?: number | null;
    convertedUsers?: Array<TrialConversionReportQueryEntry | null> | null;
}

interface TrialConversionReportQueryEntry {
    __typename?: string;
    email?: string | null;
    trialCreatedAt?: string | null;
    trialLicenseId?: number | null;
    trialLicenseName?: string | null;
    trialSubscriptionId?: number | null;
    trialSubscriptionName?: string | null;
    convertedAt?: string | null;
    convertedLicenseId?: number | null;
    convertedLicenseName?: string | null;
    totalAmountPaid?: number | null;
    convertedSubscriptionId?: number | null;
    convertedSubscriptionName?: string | null;
    conversionType?: string | null;
    title?: string | null;
}

export function TrialConversionReport() {
    const defaultTrialLicenseId = 2126;
    const [from, setFrom] = useState<moment.Moment | null>(moment().subtract(1, 'year'));
    const [to, setTo] = useState<moment.Moment | null>(moment());
    const [selectedMetricId, setSelectedMetricId] = useState<TrialConversionMetricId | null>(null);
    const hasInvalidDateRange = !!from && !!to && to.isBefore(from, 'day');
    const today = moment();

    const shouldDisableFromDate = (date: moment.Moment) => {
        if (date.isAfter(today, 'day')) {
            return true;
        }
        if (to && date.isAfter(to, 'day')) {
            return true;
        }
        return false;
    };

    const shouldDisableToDate = (date: moment.Moment) => {
        if (date.isAfter(today, 'day')) {
            return true;
        }
        if (from && date.isBefore(from, 'day')) {
            return true;
        }
        return false;
    };

    const { loading, data, error } = useQuery(GET_TRIAL_CONVERSION_REPORT, {
        variables: {
            fromDate: from?.toISOString() ?? '',
            toDate: to?.toISOString() ?? '',
            defaultTrialLicenseId,
            subscriptionIds: [],
        },
        skip: !from || !to || hasInvalidDateRange,
    });

    const model = toTrialConversionReportModel(data?.model);

    const columns: GridColDef<TrialConversionTableRow>[] = [
        {
            field: 'metric',
            headerName: 'Måling',
            minWidth: 300,
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'value',
            headerName: 'Antall',
            minWidth: 180,
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            type: 'number',
            renderCell: (params) => {
                if (!params.row.supportsDrillDown) {
                    return params.value;
                }

                return (
                    <Link
                        component="button"
                        underline="hover"
                        onClick={() => setSelectedMetricId(params.row.id)}>
                        {params.value}
                    </Link>
                );
            },
        },
    ];

    const rows: TrialConversionTableRow[] = model
        ? [
            {
                id: 'totalTrialUsersInPeriod',
                metric: 'Totalt antall prøvebrukere i perioden',
                value: model.totalTrialUsersInPeriod,
                supportsDrillDown: false,
            },
            {
                id: 'trialUsersWithPaidSubscription',
                metric: 'Prøvebrukere med betalt abonnement',
                value: model.trialUsersWithPaidSubscription,
                supportsDrillDown: true,
            },
            {
                id: 'trialUsersWithSingleDocumentAccess',
                metric: 'Prøvebrukere med enkeltanvisning',
                value: model.trialUsersWithSingleDocumentAccess,
                supportsDrillDown: true,
            },
            {
                id: 'trialUsersWithBoth',
                metric: 'Prøvebrukere med både abonnement og enkeltanvisning',
                value: model.trialUsersWithBoth,
                supportsDrillDown: true,
            },
            {
                id: 'trialUsersWithAnyPurchase',
                metric: 'Prøvebrukere med minst ett kjøp',
                value: model.trialUsersWithAnyPurchase,
                supportsDrillDown: true,
            },
        ]
        : [];

    const detailRows = getDetailRows(model?.convertedUsers ?? []);
    const selectedMetric = rows.find((row) => row.id === selectedMetricId) ?? null;
    const filteredDetailRows = detailRows.filter((row) => matchesMetric(row, selectedMetricId));

    const detailColumns: GridColDef<TrialConversionDetailRow>[] = [
        {
            field: 'paidCustomerNames',
            headerName: 'Kunder med kjøp',
            minWidth: 260,
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {params.row.paidCustomers.map((customer, index) => {
                        const key = `${customer.licenseId ?? 'unknown'}-${customer.name}`;
                        const separator = index < params.row.paidCustomers.length - 1 ? ',' : '';

                        if (!customer.licenseId) {
                            return (
                                <Typography component="span" key={key} variant="body2">
                                    {customer.name}
                                    {separator}
                                </Typography>
                            );
                        }

                        return (
                            <Typography component="span" key={key} variant="body2">
                                <RouterLink to={`/license/${customer.licenseId}`} target="_blank" rel="noreferrer">
                                    {customer.name}
                                </RouterLink>
                                {separator}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'paidSubscriptionNames',
            headerName: 'Betalte produkter',
            minWidth: 260,
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'totalAmountPaid',
            headerName: 'Totalt betalt',
            minWidth: 180,
            flex: 0.6,
            align: 'left',
            headerAlign: 'left',
            type: 'number',
            valueFormatter: (value) => formatCurrency(typeof value === 'number' ? value : 0),
        },
    ];

    return (
        <Stack spacing={2}>
            <Grid container spacing={3} sx={{ alignItems: 'center' }}>
                <Grid>
                    <DatePicker
                        disableFuture
                        label="Fra dato"
                        value={from}
                        maxDate={to ?? undefined}
                        shouldDisableDate={shouldDisableFromDate}
                        onChange={(newFrom) => {
                            setFrom(newFrom);
                            if (newFrom && to && to.isBefore(newFrom, 'day')) {
                                setTo(newFrom);
                            }
                        }}
                    />
                </Grid>
                <Grid>
                    <DatePicker
                        disableFuture
                        label="Til dato"
                        value={to}
                        minDate={from ?? undefined}
                        shouldDisableDate={shouldDisableToDate}
                        onChange={(newTo) => {
                            setTo(newTo);
                        }}
                    />
                </Grid>
                <Grid sx={{ marginLeft: 'auto' }}>{model && <TrialConversionExcelExport model={model} />}</Grid>
            </Grid>

            {hasInvalidDateRange && <Typography color="error">Til dato må være lik eller etter fra dato.</Typography>}

            {loading && <LoadingSpinner />}
            {error && <Typography color="error">{error.message}</Typography>}

            {model && (
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Klikk på antallet i kjøpsradene for å se hvilke brukere som ligger bak statistikken og hva de har kjøpt.
                    </Typography>

                    <DataGridPro
                        autoHeight
                        density="compact"
                        rows={rows}
                        columns={columns}
                        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
                        pagination
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 5,
                                    page: 0,
                                },
                            },
                        }}
                        sx={{
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 700,
                            },
                        }}
                    />

                    {selectedMetric && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <div>
                                        <Typography variant="h6">{selectedMetric.metric}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {filteredDetailRows.length} brukere matcher valgt måling.
                                        </Typography>
                                    </div>

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TrialConversionDetailExcelExport
                                            rows={filteredDetailRows}
                                            metric={selectedMetric.metric}
                                        />
                                        <Button variant="text" onClick={() => setSelectedMetricId(null)}>
                                            Lukk detaljer
                                        </Button>
                                    </Stack>
                                </Stack>

                                <DataGridPro
                                    autoHeight
                                    density="compact"
                                    rows={filteredDetailRows}
                                    columns={detailColumns}
                                    localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
                                    pagination
                                    pageSizeOptions={[10, 25, 50]}
                                    initialState={{
                                        pagination: {
                                            paginationModel: {
                                                pageSize: 10,
                                                page: 0,
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            fontWeight: 700,
                                        },
                                    }}
                                />
                            </Stack>
                        </Paper>
                    )}
                </Stack>
            )}
        </Stack>
    );
}

function TrialConversionExcelExport({ model }: { model: TrialConversionReportModel }) {
    const exportFile = () => {
        const workbook = XLSX.utils.book_new();
        const headings: string[] = ['Måling', 'Antall'];
        const rows = [
            ['Totalt antall prøvebrukere i perioden', model.totalTrialUsersInPeriod],
            ['Prøvebrukere med betalt abonnement', model.trialUsersWithPaidSubscription],
            ['Prøvebrukere med enkeltanvisning', model.trialUsersWithSingleDocumentAccess],
            ['Prøvebrukere med både abonnement og enkeltanvisning', model.trialUsersWithBoth],
            ['Prøvebrukere med minst ett kjøp', model.trialUsersWithAnyPurchase],
        ];

        const summaryInput = [headings, ...rows];
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryInput);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'PRØVEKONVERTERING');

        const detailHeadings = [
            'Kunder med kjøp',
            'Betalte produkter',
            'Totalt betalt',
        ];
        const detailRows = getDetailRows(model.convertedUsers).map((row) => [
            row.paidCustomerNames,
            row.paidSubscriptionNames,
            formatCurrency(row.totalAmountPaid),
        ]);
        const detailSheet = XLSX.utils.aoa_to_sheet([detailHeadings, ...detailRows]);
        XLSX.utils.book_append_sheet(workbook, detailSheet, 'KJØPSDETALJER');

        XLSX.writeFile(workbook, 'provekonvertering-rapport.xlsx');
    };

    return (
        <IconButton onClick={exportFile} title="Eksport til Excel" size="large">
            <ExcelLogo
                style={{
                    width: 24,
                    height: 24,
                }}
            />
        </IconButton>
    );
}

function TrialConversionDetailExcelExport({ rows, metric }: { rows: TrialConversionDetailRow[]; metric: string }) {
    const exportFile = () => {
        const workbook = XLSX.utils.book_new();
        const headings = ['Kunder med kjøp', 'Betalte produkter', 'Totalt betalt'];
        const data = rows.map((row) => [row.paidCustomerNames, row.paidSubscriptionNames, formatCurrency(row.totalAmountPaid)]);
        const sheet = XLSX.utils.aoa_to_sheet([headings, ...data]);

        XLSX.utils.book_append_sheet(workbook, sheet, 'KJØPSDETALJER');
        XLSX.writeFile(workbook, `${toExcelFileName(metric)}.xlsx`);
    };

    return (
        <IconButton onClick={exportFile} title="Eksport detaljer til Excel" size="large">
            <ExcelLogo
                style={{
                    width: 24,
                    height: 24,
                }}
            />
        </IconButton>
    );
}

function getDetailRows(entries: TrialConversionReportEntry[]): TrialConversionDetailRow[] {
    const rows = new Map<string, {
        email: string;
        trialCreatedAt: string;
        trialLicenseName: string;
        trialSubscriptionName: string;
        paidCustomers: TrialConversionCustomerLink[];
        paidCustomerNames: string[];
        paidSubscriptionNames: string[];
        totalAmountPaid: number;
        totalAmountPaidLicenseIds: Set<number>;
        singleDocumentTitles: string[];
        singleDocumentCount: number;
        firstPurchaseAt: string;
        lastPurchaseAt: string;
        hasPaidSubscription: boolean;
        hasSingleDocumentAccess: boolean;
    }>();

    entries.forEach((entry) => {
        const existingRow = rows.get(entry.email);
        const row = existingRow ?? {
            email: entry.email,
            trialCreatedAt: formatDate(entry.trialCreatedAt),
            trialLicenseName: entry.trialLicenseName,
            trialSubscriptionName: entry.trialSubscriptionName,
            paidCustomers: [],
            paidCustomerNames: [],
            paidSubscriptionNames: [],
            totalAmountPaid: 0,
            totalAmountPaidLicenseIds: new Set<number>(),
            singleDocumentTitles: [],
            singleDocumentCount: 0,
            firstPurchaseAt: formatDate(entry.convertedAt),
            lastPurchaseAt: formatDate(entry.convertedAt),
            hasPaidSubscription: false,
            hasSingleDocumentAccess: false,
        };

        if (isEarlier(entry.convertedAt, row.firstPurchaseAt)) {
            row.firstPurchaseAt = formatDate(entry.convertedAt);
        }

        if (isLater(entry.convertedAt, row.lastPurchaseAt)) {
            row.lastPurchaseAt = formatDate(entry.convertedAt);
        }

        if (entry.conversionType === 'PaidSubscription') {
            row.hasPaidSubscription = true;
            pushUniqueCustomer(row.paidCustomers, {
                licenseId: entry.convertedLicenseId,
                name: entry.convertedLicenseName ?? 'Ukjent kunde',
            });
            pushUnique(row.paidCustomerNames, entry.convertedLicenseName ?? 'Ukjent kunde');
            pushUnique(row.paidSubscriptionNames, entry.convertedSubscriptionName ?? 'Ukjent abonnement');
        }

        if (entry.conversionType === 'SingleDocumentAccess') {
            row.hasSingleDocumentAccess = true;
            pushUniqueCustomer(row.paidCustomers, {
                licenseId: entry.convertedLicenseId,
                name: entry.convertedLicenseName ?? 'Ukjent kunde',
            });
            pushUnique(row.paidCustomerNames, entry.convertedLicenseName ?? 'Ukjent kunde');
            pushUnique(row.paidSubscriptionNames, 'Tilgang til enkelt anvisninger');
            row.singleDocumentCount += 1;
            pushUnique(row.singleDocumentTitles, entry.title ?? 'Ukjent enkeltanvisning');
        }

        if (
            entry.convertedLicenseId !== null &&
            !row.totalAmountPaidLicenseIds.has(entry.convertedLicenseId)
        ) {
            row.totalAmountPaid += entry.totalAmountPaid;
            row.totalAmountPaidLicenseIds.add(entry.convertedLicenseId);
        }

        rows.set(entry.email, row);
    });

    return Array.from(rows.values())
        .map((row) => ({
            id: row.email,
            paidCustomers: row.paidCustomers,
            paidCustomerNames: formatList(row.paidCustomerNames),
            paidSubscriptionNames: formatList(row.paidSubscriptionNames),
            totalAmountPaid: row.totalAmountPaid,
            hasPaidSubscription: row.hasPaidSubscription,
            hasSingleDocumentAccess: row.hasSingleDocumentAccess,
        }))
        .sort((left, right) => left.id.localeCompare(right.id));
}

function matchesMetric(row: TrialConversionDetailRow, metricId: TrialConversionMetricId | null) {
    switch (metricId) {
        case 'trialUsersWithPaidSubscription':
            return row.hasPaidSubscription;
        case 'trialUsersWithSingleDocumentAccess':
            return row.hasSingleDocumentAccess;
        case 'trialUsersWithBoth':
            return row.hasPaidSubscription && row.hasSingleDocumentAccess;
        case 'trialUsersWithAnyPurchase':
            return row.hasPaidSubscription || row.hasSingleDocumentAccess;
        default:
            return false;
    }
}

function pushUnique(values: string[], value: string) {
    if (!values.includes(value)) {
        values.push(value);
    }
}

function pushUniqueCustomer(values: TrialConversionCustomerLink[], value: TrialConversionCustomerLink) {
    const exists = values.some((customer) => customer.licenseId === value.licenseId && customer.name === value.name);

    if (!exists) {
        values.push(value);
    }
}

function formatList(values: string[]) {
    return values.length > 0 ? values.join(', ') : '-';
}

function formatDate(value: string) {
    return moment(value).format('YYYY-MM-DD');
}

function toExcelFileName(metric: string) {
    return metric
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'kjopsdetaljer';
}

function toTrialConversionReportModel(model: GetTrialConversionReportQuery['model']): TrialConversionReportModel | null {
    if (!model) {
        return null;
    }

    return {
        totalTrialUsersInPeriod: model.totalTrialUsersInPeriod ?? 0,
        trialUsersWithPaidSubscription: model.trialUsersWithPaidSubscription ?? 0,
        trialUsersWithSingleDocumentAccess: model.trialUsersWithSingleDocumentAccess ?? 0,
        trialUsersWithBoth: model.trialUsersWithBoth ?? 0,
        trialUsersWithAnyPurchase: model.trialUsersWithAnyPurchase ?? 0,
        convertedUsers: toTrialConversionReportEntries(model.convertedUsers),
    };
}

function toTrialConversionReportEntries(entries: NonNullable<GetTrialConversionReportQuery['model']>['convertedUsers']): TrialConversionReportEntry[] {
    return (entries ?? []).flatMap((entry) => {
        if (
            !entry?.email ||
            !entry.trialCreatedAt ||
            entry.trialLicenseId === undefined ||
            entry.trialLicenseId === null ||
            !entry.trialLicenseName ||
            entry.trialSubscriptionId === undefined ||
            entry.trialSubscriptionId === null ||
            !entry.trialSubscriptionName ||
            !entry.convertedAt ||
            (entry.conversionType !== 'PaidSubscription' && entry.conversionType !== 'SingleDocumentAccess')
        ) {
            return [];
        }

        return [{
            email: entry.email,
            trialCreatedAt: entry.trialCreatedAt,
            trialLicenseId: entry.trialLicenseId,
            trialLicenseName: entry.trialLicenseName,
            trialSubscriptionId: entry.trialSubscriptionId,
            trialSubscriptionName: entry.trialSubscriptionName,
            convertedAt: entry.convertedAt,
            convertedLicenseId: entry.convertedLicenseId ?? null,
            convertedLicenseName: entry.convertedLicenseName ?? null,
            totalAmountPaid: entry.totalAmountPaid ?? 0,
            convertedSubscriptionId: entry.convertedSubscriptionId ?? null,
            convertedSubscriptionName: entry.convertedSubscriptionName ?? null,
            conversionType: entry.conversionType,
            title: entry.title ?? null,
        }];
    });
}

function formatCurrency(value: number) {
    return value.toLocaleString('nb-NO', {
        style: 'currency',
        currency: 'NOK',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function isEarlier(left: string, right: string) {
    return moment(left).isBefore(moment(right));
}

function isLater(left: string, right: string) {
    return moment(left).isAfter(moment(right));
}
